import { parseFigmaFrameReference } from '@/features/cogo-xyz/figma-token-connection'

export type StudioSourceKind =
  | 'assistant_prompt'
  | 'url_import'
  | 'ide_file'
  | 'github_pr'
  | 'figma_frame'
  | 'figma_file'
  | 'document_file'

export type SourcePurpose =
  | 'auto'
  | 'design-reference'
  | 'content-data'
  | 'requirements'

export type SourceIngestPhase =
  | 'idle'
  | 'detected'
  | 'extracting'
  | 'analyzing'
  | 'ready'
  | 'needs-action'
  | 'error'

export type SourceRecoveryAction =
  | 'choose-file'
  | 'connect-figma'
  | 'open-project'
  | 'retry'
  | 'sign-in'
  | null

export interface SourceIngestGuidance {
  phase: SourceIngestPhase
  sourceKind: StudioSourceKind
  provenanceReady: boolean
  suggestedPurpose: Exclude<SourcePurpose, 'auto'>
  resolvedPurpose: Exclude<SourcePurpose, 'auto'>
  provenanceKey:
    | 'prompt'
    | 'url'
    | 'code'
    | 'github'
    | 'figma-frame'
    | 'figma-file'
    | 'document'
  authorityKey:
    | 'instruction'
    | 'external-reference'
    | 'copied-code'
    | 'repository-readback'
    | 'figma-rebuild'
    | 'retained-design'
    | 'normalized-document'
  usageKey:
    | 'generate'
    | 'reference'
    | 'review-code'
    | 'review-change'
    | 'rebuild-design'
    | 'inspect-design'
    | 'inspect-document'
  nextActionKey:
    | 'add-source'
    | 'review-and-run'
    | 'wait'
    | 'open-result'
    | 'choose-another-file'
    | 'connect-figma'
    | 'open-project'
    | 'retry'
  recoveryAction: SourceRecoveryAction
}

export interface BuildSourceIngestGuidanceInput {
  sourceKind: StudioSourceKind
  selectedPurpose: SourcePurpose
  hasInput: boolean
  running: boolean
  readingFile: boolean
  figmaFileAvailable: boolean
  figmaFrameAvailable: boolean
  frameNeedsProject: boolean
  figmaTokenReady: boolean
  hasImportFailure: boolean
  hasDocumentError: boolean
  hasOperationError: boolean
  documentReady: boolean
  figmaFileReady: boolean
  figmaFrameReady: boolean
}

const FIGMA_URL = /^https?:\/\/(?:www\.)?figma\.com\//i
const GITHUB_PR_URL = /^https?:\/\/(?:www\.)?github\.com\/[^/]+\/[^/]+\/pull\/\d+(?:[/?#].*)?$/i
const GITHUB_PR_SHORTHAND = /^[\w.-]+\/[\w.-]+#\d+$/

export function detectStudioSourceKind(value: string): StudioSourceKind {
  const trimmed = value.trim()
  if (!trimmed) return 'assistant_prompt'
  if (FIGMA_URL.test(trimmed) && parseFigmaFrameReference(trimmed)) {
    return 'figma_frame'
  }
  if (GITHUB_PR_URL.test(trimmed) || GITHUB_PR_SHORTHAND.test(trimmed)) {
    return 'github_pr'
  }
  try {
    const url = new URL(trimmed)
    if (url.protocol === 'http:' || url.protocol === 'https:') {
      return 'url_import'
    }
  } catch {
    // A normal request remains an agent prompt. Detection never guesses a
    // source adapter from prose or a partial URL.
  }
  return 'assistant_prompt'
}

export function suggestSourcePurpose(
  kind: StudioSourceKind,
): Exclude<SourcePurpose, 'auto'> {
  if (kind === 'figma_file' || kind === 'figma_frame') {
    return 'design-reference'
  }
  if (kind === 'document_file' || kind === 'github_pr') {
    return 'requirements'
  }
  if (kind === 'url_import') return 'content-data'
  if (kind === 'ide_file') return 'design-reference'
  return 'requirements'
}

function sourceEvidence(kind: StudioSourceKind): Pick<
  SourceIngestGuidance,
  'provenanceKey' | 'authorityKey' | 'usageKey'
> {
  switch (kind) {
    case 'url_import':
      return {
        provenanceKey: 'url',
        authorityKey: 'external-reference',
        usageKey: 'reference',
      }
    case 'ide_file':
      return {
        provenanceKey: 'code',
        authorityKey: 'copied-code',
        usageKey: 'review-code',
      }
    case 'github_pr':
      return {
        provenanceKey: 'github',
        authorityKey: 'repository-readback',
        usageKey: 'review-change',
      }
    case 'figma_frame':
      return {
        provenanceKey: 'figma-frame',
        authorityKey: 'figma-rebuild',
        usageKey: 'rebuild-design',
      }
    case 'figma_file':
      return {
        provenanceKey: 'figma-file',
        authorityKey: 'retained-design',
        usageKey: 'inspect-design',
      }
    case 'document_file':
      return {
        provenanceKey: 'document',
        authorityKey: 'normalized-document',
        usageKey: 'inspect-document',
      }
    default:
      return {
        provenanceKey: 'prompt',
        authorityKey: 'instruction',
        usageKey: 'generate',
      }
  }
}

export function buildSourceIngestGuidance(
  input: BuildSourceIngestGuidanceInput,
): SourceIngestGuidance {
  const suggestedPurpose = suggestSourcePurpose(input.sourceKind)
  const resolvedPurpose =
    input.selectedPurpose === 'auto'
      ? suggestedPurpose
      : input.selectedPurpose
  const evidence = sourceEvidence(input.sourceKind)
  const selectedSourceReady =
    input.sourceKind === 'document_file'
      ? input.documentReady
      : input.sourceKind === 'figma_file'
        ? input.figmaFileReady
        : input.sourceKind === 'figma_frame'
          ? input.figmaFrameReady
          : false

  let phase: SourceIngestPhase = input.hasInput ? 'detected' : 'idle'
  let nextActionKey: SourceIngestGuidance['nextActionKey'] = input.hasInput
    ? 'review-and-run'
    : 'add-source'
  let recoveryAction: SourceRecoveryAction = null

  if (
    input.hasImportFailure ||
    input.hasDocumentError ||
    input.hasOperationError
  ) {
    phase = 'error'
    nextActionKey =
      input.sourceKind === 'figma_file' ||
      input.sourceKind === 'document_file'
        ? 'choose-another-file'
        : 'retry'
    recoveryAction =
      input.sourceKind === 'figma_file' ||
      input.sourceKind === 'document_file'
        ? 'choose-file'
        : 'retry'
  } else if (input.running || input.readingFile) {
    phase =
      input.sourceKind === 'figma_file' ||
      input.sourceKind === 'document_file' ||
      input.sourceKind === 'url_import'
        ? 'extracting'
        : 'analyzing'
    nextActionKey = 'wait'
  } else if (selectedSourceReady) {
    phase = 'ready'
    nextActionKey = 'open-result'
  } else if (input.sourceKind === 'figma_file' && !input.figmaFileAvailable) {
    phase = 'needs-action'
    nextActionKey = 'choose-another-file'
    recoveryAction = 'choose-file'
  } else if (
    input.sourceKind === 'figma_frame' &&
    !input.figmaFrameAvailable
  ) {
    phase = 'needs-action'
    nextActionKey = 'connect-figma'
    recoveryAction = 'connect-figma'
  } else if (input.sourceKind === 'figma_frame' && input.frameNeedsProject) {
    phase = 'needs-action'
    nextActionKey = 'open-project'
    recoveryAction = 'open-project'
  } else if (
    input.sourceKind === 'figma_frame' &&
    input.hasInput &&
    !input.figmaTokenReady
  ) {
    phase = 'needs-action'
    nextActionKey = 'connect-figma'
    recoveryAction = 'connect-figma'
  }

  return {
    phase,
    sourceKind: input.sourceKind,
    provenanceReady:
      input.sourceKind === 'assistant_prompt' || input.sourceKind === 'ide_file'
        ? input.hasInput && !input.readingFile
        : selectedSourceReady,
    suggestedPurpose,
    resolvedPurpose,
    ...evidence,
    nextActionKey,
    recoveryAction,
  }
}

export function sourcePurposeTag(
  purpose: Exclude<SourcePurpose, 'auto'>,
): string {
  return `source-purpose:${purpose}`
}

export function explainSourceOperationError(error: unknown): {
  messageKey: 'sign-in' | 'service' | 'permission' | 'retry'
  technicalDetail: string | null
} {
  const detail = error instanceof Error ? error.message : String(error ?? '')
  const normalized = detail.toLowerCase()
  if (normalized.includes('authenticated source export is not configured')) {
    return { messageKey: 'service', technicalDetail: detail || null }
  }
  if (
    (normalized.includes('source export requires') &&
      normalized.includes('token'))
  ) {
    return { messageKey: 'sign-in', technicalDetail: detail || null }
  }
  if (
    normalized.includes('not configured') ||
    normalized.includes('service-not-configured')
  ) {
    return { messageKey: 'service', technicalDetail: detail || null }
  }
  if (
    normalized.includes('returned 401') ||
    normalized.includes('returned 403') ||
    normalized.includes('permission')
  ) {
    return { messageKey: 'permission', technicalDetail: detail || null }
  }
  return { messageKey: 'retry', technicalDetail: detail || null }
}
